"use client";

import React from "react";
import {
  Phone,
  Truck,
  ShoppingCart,
  MapPin,
  CheckCircle,
  ArrowRight,
  Globe,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface HowItWorksStep {
  step: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface EquipmentHowItWorksProps {
  title?: string;
  subtitle?: string;
  steps?: HowItWorksStep[];
}

const EquipmentHowItWorks: React.FC<EquipmentHowItWorksProps> = ({
  title = "How It Works",
  subtitle = "Simple steps to get the equipment you need",
  steps = [
    {
      step: 1,
      icon: <div className="flex space-x-2">
        <Globe className="w-6 h-6" />
        <Phone className="w-6 h-6" />
      </div>,
      title: "Order Online or Call",
      description:
        "Place your order and complete payment through our website or call us at 04 77 746 329 to book and pay",
    },
    {
      step: 2,
      icon: <ShoppingCart className="w-8 h-8" />,
      title: "Confirm Your Booking",
      description:
        "We'll confirm your booking details and rental dates for your equipment",
    },
    {
      step: 3,
      icon: <Truck className="w-8 h-8" />,
      title: "Pickup or Delivery",
      description:
        "Choose pickup to collect your equipment, or select delivery and we'll bring it to your location",
    },
  ],
}) => {
  return (
    <Card className="overflow-hidden shadow-sm hover:shadow-md transition-shadow mb-8">
      <CardContent className="pt-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-bold mb-2 text-black">
            {title}
          </h2>
          <p className="text-sm text-gray-600">
            {subtitle}
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <div key={step.step} className="relative">
              {/* Step Card */}
              <div className="text-center">
                {/* Step Number */}
                <div className="flex justify-center mb-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--color-rr-green)] flex items-center justify-center text-white font-bold text-sm">
                    {step.step}
                  </div>
                </div>

                {/* Icon */}
                <div className="flex justify-center mb-3">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-[var(--color-rr-green)]">
                    {step.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-sm font-semibold text-black mb-2">
                  {step.title}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Arrow (desktop only) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 -right-3 transform -translate-y-1/2">
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Options */}
        <div className="grid md:grid-cols-2 gap-4 mt-8 pt-6 border-t border-gray-200">
          {/* Pickup Option */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center mb-2">
              <MapPin className="w-4 h-4 text-blue-600 mr-2" />
              <h4 className="text-sm font-semibold text-blue-900">Pickup Option</h4>
            </div>
            <p className="text-xs text-blue-700">You collect the equipment from our location at your scheduled time</p>
          </div>

          {/* Delivery Option */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center mb-2">
              <Truck className="w-4 h-4 text-green-600 mr-2" />
              <h4 className="text-sm font-semibold text-green-900">Delivery Option</h4>
            </div>
            <p className="text-xs text-green-700">We deliver to your location ($50) and pickup when you're done ($50)</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EquipmentHowItWorks;